import { TextInput, Button, Flex, Card, NumberInput, Box } from '@applyboard/crystal-ui';
import { ArrowRightOutlineIcon, DeleteOutlineIcon } from '@applyboard/ui-icons';
import { RuleWithId, GradeSchemaInfo, GradePointMapping } from '~/types/gradeConversion';
import { ConversionTableRule } from '@ab/ams-school-api/src/services/content/v1/ConversionTables/types';

interface LetterDestinationRulesTableProps {
  rules: RuleWithId[];
  gradeSchema: GradeSchemaInfo;
  gradePointMappings: GradePointMapping[];
  onUpdateRule: (index: number, updates: Partial<ConversionTableRule>) => void;
  onRemoveRule: (index: number) => void;
}

export function LetterDestinationRulesTable({
  rules,
  gradeSchema,
  gradePointMappings,
  onUpdateRule,
  onRemoveRule,
}: LetterDestinationRulesTableProps) {
  const isNumericSource = gradeSchema.gradeSchemaType === 'numeric';
  const validDestinationGrades = gradePointMappings
    .map((gpm) => gpm.letterGrade)
    .filter((grade): grade is string => Boolean(grade));

  return (
    <Flex direction="column" gap={4}>
      {rules.map((rule, rowIndex) => (
        <Card key={rule.tempId ?? rowIndex} emphasis="outlined">
          <Flex direction="column" gap={3}>
            <Flex direction="row" gap={4} align="center" wrap>
              {isNumericSource ? (
                <>
                  <Flex.Item grow={1} basis="240px">
                    <NumberInput
                      onChange={(value) =>
                        onUpdateRule(rowIndex, {
                          sourceRangeLow: value ? parseFloat(value) : undefined,
                        })
                      }
                      value={rule.sourceRangeLow?.toString() ?? ''}
                      label="Source Range Low"
                      size="sm"
                    />
                  </Flex.Item>
                  <Flex.Item grow={1} basis="240px">
                    <NumberInput
                      onChange={(value) =>
                        onUpdateRule(rowIndex, {
                          sourceRangeHigh: value ? parseFloat(value) : undefined,
                        })
                      }
                      value={rule.sourceRangeHigh?.toString() ?? ''}
                      label="Source Range High"
                      size="sm"
                    />
                  </Flex.Item>
                </>
              ) : (
                <Flex.Item grow={1} basis="240px">
                  <TextInput
                    onChange={(value) =>
                      onUpdateRule(rowIndex, {
                        sourceLetterGrade: value,
                      })
                    }
                    value={rule.sourceLetterGrade ?? ''}
                    label="Source Letter Grade"
                    size="sm"
                    placeholder="e.g., A"
                  />
                </Flex.Item>
              )}

              <Flex.Item align="center">
                <Box pt={8}>
                  <ArrowRightOutlineIcon />
                </Box>
              </Flex.Item>

              <Flex.Item grow={1} basis="240px">
                <TextInput
                  onChange={(value) =>
                    onUpdateRule(rowIndex, {
                      destinationLetterGrade: value,
                    })
                  }
                  value={rule.destinationLetterGrade ?? ''}
                  label="Destination Letter Grade"
                  size="sm"
                  placeholder={validDestinationGrades.length ? `e.g., ${validDestinationGrades[0]}` : undefined}
                />
              </Flex.Item>

              <Button
                size="sm"
                onClick={() => onRemoveRule(rowIndex)}
                aria-label="Delete Rule"
                leadIcon={DeleteOutlineIcon}
                intent="negative"
                emphasis="transparent"
                disabled={rules.length === 1}
              />
            </Flex>
          </Flex>
        </Card>
      ))}
    </Flex>
  );
}
